// ─── El kit: un artículo que se vende armado y se compra por partes ─────────
// Hay DOS clases, y `componentesDeArticulo` las unifica:
//   · el kit de CATÁLOGO (receta fija en la ficha del producto: el "Set
//     escolar" de 2 cuadernos y 3 lápices) — ver kitCatalogo.js;
//   · el kit de LÍNEA (composición negociada en el documento) — lo que sigue.
// La línea manda sobre el catálogo: un set se arma siempre igual, salvo en el
// pedido donde se acordó otra cosa.
//
// Mathiz le vende al cliente UN artículo —"Suculenta"— y le compra al proveedor
// DOS —la matera y la mata viva—. La composición cambia en cada pedido, así que
// no vive en el catálogo: vive en la LÍNEA del documento, como los costos de esa
// línea. Un costo que apunta a un producto del catálogo (`articulo`) es un
// componente; uno de texto libre (marcación, transporte) no lo es.
//
// De ahí salen las dos cosas que este módulo resuelve:
//
//   1. CUÁNTO SE PUEDE ENTREGAR. El kit nunca entra al kardex —se arma al
//      entregar— así que su stock es cero por definición. Preguntar por el
//      stock de "Suculenta" bloquearía la remisión para siempre. Lo que manda
//      es el componente que menos alcance.
//
//   2. QUÉ SALE DE BODEGA AL ENTREGAR. Salen los componentes, no el kit, y en
//      proporción a lo entregado: de 100 suculentas pedidas, entregar 40
//      consume 40 materas y 40 matas. El costo de venta es la suma de los
//      costos promedio reales de esos componentes, no un costo digitado a mano.
//
// Todo esto solo se activa cuando la orden tiene componentes de verdad. Una
// orden sin ellos —todas las que existen hoy— se comporta exactamente igual que
// antes: se descuenta el artículo vendido y se costea con su costo promedio.
import { getCostoPromedio, getStockDisponible } from "./accounting.js";
import { componentesDeProducto, productoPorNombre } from "./kitCatalogo.js";
import { state } from "./state.js";
import { parseNumber } from "./utils.js";

function norm(s) {
  return String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/\s+/g, " ").trim();
}

// La cantidad que la orden pidió de un artículo. Es el divisor para pasar de
// "cuántas materas lleva el pedido entero" a "cuántas lleva una suculenta".
function cantidadPedida(op, articulo) {
  const it = (op && op.items || []).find(x => norm(x.articulo) === norm(articulo));
  return parseNumber(it && it.cantidad) || 0;
}

// Los componentes de una línea de la orden, con su cantidad POR UNIDAD del
// artículo vendido. Un costo cuenta como componente solo si apunta a un
// producto que existe en el catálogo: sin ficha no hay kardex al que llegar.
export function componentesDeLinea(op, articulo) {
  const pedida = cantidadPedida(op, articulo);
  if (!op || !articulo || pedida <= 0) return [];
  const prods = state.data.productos || [];
  return (op._conceptosCosto || [])
    .filter(c => !c._esComision)
    .filter(c => norm(c._articuloOrigen) === norm(articulo))
    .map(c => {
      const nombre = (c.articulo || "").trim();
      if (!nombre) return null;
      const prod = prods.find(p => p.nombre === nombre) || prods.find(p => norm(p.nombre) === norm(nombre));
      if (!prod) return null;
      const total = parseNumber(c.cantidad) || 0;
      if (total <= 0) return null;
      return { articulo: prod.nombre, porUnidad: total / pedida, totalPedido: total };
    })
    .filter(Boolean);
}

// ─── La puerta: de dónde salen los componentes de un artículo ───────────────
// Hay dos clases de kit y esta función las unifica, en este orden:
//
//   1. El kit de LÍNEA — los costos de esa línea que apuntan a un producto
//      (la suculenta). Manda porque es lo que se negoció para ESTE pedido.
//   2. El kit de CATÁLOGO — la receta fija de la ficha (el set escolar).
//
// Que la línea mande sobre el catálogo es la regla que permite las dos cosas a
// la vez: un set escolar se arma siempre igual, pero si en un pedido concreto
// se cambió su contenido, ese pedido se entrega con lo que se cotizó.
export function componentesDeArticulo(op, articulo) {
  const deLinea = componentesDeLinea(op, articulo);
  if (deLinea.length) return deLinea;
  return componentesDeProducto(productoPorNombre(articulo))
    .map(c => ({ articulo: c.articulo, porUnidad: c.cantidad, totalPedido: null }));
}

export function esKit(op, articulo) {
  return componentesDeArticulo(op, articulo).length > 0;
}

// Cuántas unidades del artículo vendido se pueden armar con lo que hay en
// bodega. Manda el componente que menos alcance: con 80 materas y 200 matas se
// pueden armar 80 suculentas, no 200.
export function stockDisponibleKit(op, articulo, exceptoOpId) {
  const comps = componentesDeArticulo(op, articulo);
  if (!comps.length) return null;          // no es un kit: no opina
  return comps.reduce((min, c) => {
    const disp = getStockDisponible(c.articulo, exceptoOpId);
    const armables = c.porUnidad > 0 ? Math.floor(disp / c.porUnidad) : 0;
    return Math.min(min, armables);
  }, Infinity);
}

// Lo que sale de bodega al entregar `cantidad` unidades del kit, y a qué costo.
// Las cantidades se redondean al entero más cercano: no se puede sacar media
// matera del kardex.
export function consumoParaEntrega(op, articulo, cantidad) {
  const cant = parseNumber(cantidad) || 0;
  if (cant <= 0) return [];
  return componentesDeArticulo(op, articulo).map(c => ({
    articulo: c.articulo,
    cantidad: Math.round(c.porUnidad * cant),
    costoUnitario: getCostoPromedio(c.articulo)
  })).filter(x => x.cantidad > 0);
}

// Lo que cuesta armar UNA unidad del kit: la suma de los costos promedio
// reales de sus partes. Es el costo de ventas verdadero de la suculenta; el
// campo `costo` del catálogo es apenas un número digitado.
export function costoUnitarioKit(op, articulo) {
  const comps = componentesDeArticulo(op, articulo);
  if (!comps.length) return null;
  return comps.reduce((s, c) => s + c.porUnidad * getCostoPromedio(c.articulo), 0);
}

// Texto corto para la pantalla: "1 Matera cerámica + 1 Mata suculenta".
export function descripcionKit(op, articulo) {
  return componentesDeArticulo(op, articulo)
    .map(c => `${formatearPorUnidad(c.porUnidad)} ${c.articulo}`).join(" + ");
}

function formatearPorUnidad(n) {
  return Number.isInteger(n) ? String(n) : (Math.round(n * 100) / 100).toString();
}

// ─── Lo que de verdad sale del kardex por una remisión ──────────────────────
// Una sola puerta para el egreso: recibe las líneas de la remisión y devuelve
// los movimientos reales, ya explotados. Las líneas que no son kit salen tal
// cual, que es lo que hacía —y sigue haciendo— todo el ERP.
export function egresosDeRemision(rc, op) {
  const salidas = [];
  (rc && rc.items || []).forEach(it => {
    const cant = parseNumber(it.cantidad) || 0;
    if (cant <= 0) return;
    // Sin `op` también hay kit: el de catálogo no necesita orden de origen (una
    // remisión directa de un set escolar tiene que sacar sus cuadernos igual).
    const comps = consumoParaEntrega(op, it.articulo, cant);
    if (comps.length) {
      // Kit: salen sus partes. Se anota de qué línea vendida vienen, para que
      // el kardex diga por qué salió una matera cuando se entregó una suculenta.
      comps.forEach(c => salidas.push({ ...c, _deKit: it.articulo }));
    } else {
      salidas.push({ articulo: it.articulo, cantidad: cant,
                     costoUnitario: getCostoPromedio(it.articulo) });
    }
  });
  return salidas;
}

// ─── Lo que cuesta la entrega, por el artículo VENDIDO ──────────────────────
// Ojo con la diferencia respecto a `egresosDeRemision`: el kardex se mueve por
// componentes, pero el costo de ventas se lleva por el artículo que está en la
// factura (ver registrarCostoEntrega, que cruza contra `_costoPendiente`).
// Llevarle los componentes no cruzaría con nada y el costo quedaría sin
// registrar. Así que el kit viaja como una sola línea, con el costo de armarlo.
export function costosDeRemision(rc, op) {
  return (rc && rc.items || []).map(it => {
    const cant = parseNumber(it.cantidad) || 0;
    const cuKit = costoUnitarioKit(op, it.articulo);
    return { articulo: it.articulo, cantidad: cant,
             costoUnitario: cuKit != null ? cuKit : getCostoPromedio(it.articulo) };
  }).filter(x => x.cantidad > 0);
}
